// Editorial Grid: pins the section and scrolls the story and products columns.
// Every Editorial Grid section includes this file, so it can be executed
// several times per page; only create the singleton once.
(function () {
  if (window.EditorialGrid) return;

  var loader = null;

  // parallax.js is deferred; if it still isn't there (a section freshly added
  // in the theme editor), load it once and report whether that worked. A
  // failed load is retried by the next section that asks.
  function withParallax(src, done) {
    if (window.ThemeParallax) return done(true);
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        withParallax(src, done);
      }, { once: true });
      return;
    }
    if (!loader) {
      loader = new Promise(function (resolve) {
        var script = document.createElement('script');
        script.src = src;
        script.onload = function () { resolve(!!window.ThemeParallax); };
        script.onerror = function () {
          loader = null;
          resolve(false);
        };
        document.head.appendChild(script);
      });
    }
    loader.then(done);
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function setupSection(sectionEl) {
    var storyContainer = sectionEl.querySelector('[data-eg-story]');
    var storyTrack = sectionEl.querySelector('[data-eg-story-track]');
    var productsContainer = sectionEl.querySelector('[data-eg-products]');
    var productsTrack = sectionEl.querySelector('[data-eg-products-track]');
    var viewport = sectionEl.querySelector('[data-eg-viewport]');
    var sticky = sectionEl.querySelector('[data-eg-sticky]');

    if (!storyTrack || !productsTrack || !productsContainer || !viewport) return;

    var disablePinningMobile = sectionEl.classList.contains('editorial-grid--no-pin-mobile');
    var mobileQuery = window.matchMedia('(max-width: 749px)');

    var pinnedRegistered = false;
    var resizeTimer = null;
    var selectTimer = null;
    var focusFrame = 0;
    var pointerAt = 0;
    var observer = null;
    var touchAttached = false;
    var storyItems = imageItems(storyTrack, '.editorial-grid__story-block');
    var cardItems = imageItems(productsTrack, '.editorial-grid__card');

    // Geometry + interaction state, re-measured on init / resize / window 'load'.
    // productDistance is horizontal on mobile (products translate-X) and vertical
    // on desktop (products translate-Y). applyTransforms picks the right axis.
    var state = {
      progress: 0,
      startX: 0,
      startY: 0,
      startProgress: 0,
      storyDistance: 0,
      productDistance: 0,
      storyView: 0,
      productView: 0,
      pinStartY: 0,
      pinEndY: 0,
      headerH: 0,
      consuming: false,
      decided: false,
      multiTouch: false,
      edgeStart: false,
    };
    // The transforms last written to the tracks ('' when cleared)
    var written = { story: '', products: '' };

    function isMobile() { return mobileQuery.matches; }
    function hasParallax() { return !!window.ThemeParallax; }

    // Runs every frame while the section is in view; writes only a transform
    // that changed, so a still page writes nothing
    function applyTransforms(p) {
      state.progress = p;
      var story = 'translate3d(0, ' + (-p * state.storyDistance) + 'px, 0)';
      var products;
      if (isMobile()) {
        // Mobile: products translate horizontally
        products = 'translate3d(' + (-p * state.productDistance) + 'px, 0, 0)';
      } else {
        // Desktop: products translate vertically (drift down the column)
        products = 'translate3d(0, ' + (-p * state.productDistance) + 'px, 0)';
      }
      if (story !== written.story) {
        written.story = story;
        storyTrack.style.transform = story;
      }
      if (products !== written.products) {
        written.products = products;
        productsTrack.style.transform = products;
      }
    }

    // Story blocks and cards that have images, with their extent along the
    // track (filled in by measurePinned)
    function imageItems(track, selector) {
      var items = [];
      Array.prototype.forEach.call(track.querySelectorAll(selector), function (el) {
        var imgs = el.querySelectorAll('img');
        if (imgs.length) items.push({ el: el, imgs: imgs, start: 0, end: 0, ready: false });
      });
      return items;
    }

    function measureItems(items, track, horizontal) {
      var origin = track.getBoundingClientRect();
      items.forEach(function (item) {
        var rect = item.el.getBoundingClientRect();
        item.start = horizontal ? rect.left - origin.left : rect.top - origin.top;
        item.end = item.start + (horizontal ? rect.width : rect.height);
      });
    }

    // Safari decodes an image on the main thread when it is first painted,
    // which stutters mid-scroll; fetch and decode the images within a column's
    // length of the visible part before they are needed. Runs only while the
    // section is near the viewport, and waits for the window load so it never
    // competes with the LCP image.
    function prepare(p) {
      if (document.readyState !== 'complete') return;
      prepareItems(storyItems, p * state.storyDistance, state.storyView);
      prepareItems(cardItems, p * state.productDistance, state.productView);
    }

    function prepareItems(items, pos, view) {
      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        if (item.ready || item.end < pos - view || item.start > pos + 2 * view) continue;
        item.ready = true;
        Array.prototype.forEach.call(item.imgs, function (img) {
          img.loading = 'eager';
          if (img.decode) img.decode().catch(function () {});
        });
      }
    }

    function measurePinned() {
      // Story translates vertically on both breakpoints
      state.storyDistance = Math.max(
        0,
        storyTrack.scrollHeight - storyContainer.clientHeight
      );
      // Products: horizontal distance on mobile, vertical distance on desktop
      if (isMobile()) {
        state.productDistance = Math.max(
          0,
          productsTrack.scrollWidth - productsContainer.clientWidth
        );
      } else {
        state.productDistance = Math.max(
          0,
          productsTrack.scrollHeight - productsContainer.clientHeight
        );
      }
      state.storyView = storyContainer.clientHeight;
      state.productView = isMobile() ? productsContainer.clientWidth : productsContainer.clientHeight;
      measureItems(storyItems, storyTrack, false);
      measureItems(cardItems, productsTrack, isMobile());

      var rect = viewport.getBoundingClientRect();
      var headerStr = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
      var headerH = parseFloat(headerStr) || 0;
      state.headerH = headerH;
      state.pinStartY = rect.top + window.scrollY - headerH;
      state.pinEndY = rect.top + window.scrollY + viewport.offsetHeight - window.innerHeight;
      if (state.pinEndY < state.pinStartY) state.pinEndY = state.pinStartY;
    }

    function onPinnedTick() {
      if (state.consuming) return;
      var span = state.pinEndY - state.pinStartY;
      var p = span > 0 ? (window.scrollY - state.pinStartY) / span : 0;
      if (p < 0) p = 0;
      else if (p > 1) p = 1;
      applyTransforms(p);
    }

    // Called by parallax.js every frame while the section is near the viewport
    function onFrame() {
      onPinnedTick();
      prepare(state.progress);
    }

    function registerPinned() {
      if (pinnedRegistered || !hasParallax()) return;
      measurePinned();
      storyTrack.style.willChange = 'transform';
      productsTrack.style.willChange = 'transform';
      // disableBelow: 0 so the crossfade-stack tick runs on both breakpoints.
      // activate() guarantees we only register at the right times.
      window.ThemeParallax.register(sectionEl, {
        type: 'crossfade-stack',
        disableBelow: 0,
        onProgress: onFrame,
      });
      pinnedRegistered = true;
      // Apply the current position now: the module only ticks once its
      // IntersectionObserver reports, after the next paint, so a rebuild
      // mid-pin would otherwise show the tracks at 0 for a frame.
      onPinnedTick();
    }

    function unregisterPinned() {
      if (!pinnedRegistered || !hasParallax()) return;
      window.ThemeParallax.unregister(sectionEl);
      storyTrack.style.transform = '';
      productsTrack.style.transform = '';
      written.story = written.products = '';
      storyTrack.style.willChange = '';
      productsTrack.style.willChange = '';
      pinnedRegistered = false;
    }

    // ---- Touch handlers (mobile pinned mode only) ----

    function onTouchStart(e) {
      state.consuming = false;
      state.decided = false;
      state.multiTouch = e.touches.length !== 1;
      state.edgeStart = false;
      if (state.multiTouch) return;

      var t = e.touches[0];
      var rect = productsContainer.getBoundingClientRect();
      if (t.clientX - rect.left < 20 || rect.right - t.clientX < 20) {
        state.edgeStart = true;
        return;
      }

      state.startX = t.clientX;
      state.startY = t.clientY;
      state.startProgress = state.progress;
    }

    function onTouchMove(e) {
      if (state.multiTouch || state.edgeStart) return;
      if (e.touches.length !== 1) {
        state.multiTouch = true;
        state.consuming = false;
        return;
      }

      var t = e.touches[0];
      var dx = t.clientX - state.startX;
      var dy = t.clientY - state.startY;

      if (!state.decided) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        state.decided = true;
        state.consuming = Math.abs(dx) > Math.abs(dy);
        if (!state.consuming) return;
      }

      if (!state.consuming) return;

      e.preventDefault();

      var deltaP = state.productDistance > 0 ? -dx / state.productDistance : 0;
      var newP = state.startProgress + deltaP;
      if (newP < 0) newP = 0;
      else if (newP > 1) newP = 1;
      applyTransforms(newP);
    }

    function onTouchEnd() {
      if (state.consuming) {
        var span = state.pinEndY - state.pinStartY;
        var targetY = state.pinStartY + state.progress * span;
        window.scrollTo(0, targetY);
      }
      state.consuming = false;
      state.decided = false;
      state.multiTouch = false;
      state.edgeStart = false;
    }

    function attachTouch() {
      if (touchAttached) return;
      productsContainer.addEventListener('touchstart', onTouchStart, { passive: true });
      productsContainer.addEventListener('touchmove', onTouchMove, { passive: false });
      productsContainer.addEventListener('touchend', onTouchEnd, { passive: true });
      productsContainer.addEventListener('touchcancel', onTouchEnd, { passive: true });
      touchAttached = true;
    }

    function detachTouch() {
      if (!touchAttached) return;
      productsContainer.removeEventListener('touchstart', onTouchStart);
      productsContainer.removeEventListener('touchmove', onTouchMove);
      productsContainer.removeEventListener('touchend', onTouchEnd);
      productsContainer.removeEventListener('touchcancel', onTouchEnd);
      touchAttached = false;
      // A swipe cut short here never gets its touchend; left consuming, the
      // tick would stop following the scroll
      state.consuming = false;
      state.decided = false;
      state.multiTouch = false;
      state.edgeStart = false;
    }

    // ---- Activation: pick the right code path for current state ----

    function activate() {
      var mobile = isMobile();

      // The merchant's "Disable pinning on mobile" setting: stacked layout
      // with a native product strip
      if (mobile && disablePinningMobile) {
        unregisterPinned();
        detachTouch();
        return;
      }

      // Pinned scrollytelling on both breakpoints. Tear down + re-register so
      // the products axis is re-picked (story always vertical; products
      // horizontal on mobile, vertical on desktop). activate() is called on
      // init and when the 749px media query changes, not on plain resizes.
      // Touch goes first so registerPinned() applies the position.
      if (mobile) {
        attachTouch();
      } else {
        detachTouch();
      }
      unregisterPinned();
      registerPinned();
    }

    // Remeasure in place and re-apply the position, without re-registering
    // (which would show the tracks at 0 for a frame). iPhone Safari fires
    // resize whenever the address bar collapses or expands.
    function remeasure() {
      if (!pinnedRegistered) return;
      measurePinned();
      onPinnedTick();
    }

    // Plain resizes remeasure in place; crossing the mobile/desktop boundary
    // goes through mobileQuery's change event and activate().
    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(remeasure, 150);
    }

    // The columns are overflow: hidden, but scrollIntoView() and focus can
    // still scroll them, which pushes them out of line with the transforms
    function resetScroll() {
      [sticky, storyContainer, productsContainer].forEach(function (box) {
        if (box && (box.scrollTop || box.scrollLeft)) {
          box.scrollTop = 0;
          box.scrollLeft = 0;
        }
      });
    }

    // Scroll the page to the pin progress that shows item in its column,
    // centred when it fits
    function reveal(item) {
      var inProducts = productsContainer.contains(item);
      var container = inProducts ? productsContainer : storyContainer;
      var track = inProducts ? productsTrack : storyTrack;
      var distance = inProducts ? state.productDistance : state.storyDistance;
      var horizontal = inProducts && isMobile();
      var box = container.getBoundingClientRect();
      var trackRect = track.getBoundingClientRect();
      var rect = item.getBoundingClientRect();
      // Offset within the track; both rects carry the same transform
      var start = horizontal ? rect.left - trackRect.left : rect.top - trackRect.top;
      var size = horizontal ? rect.width : rect.height;
      var view = horizontal ? box.width : box.height;
      var p = distance > 0 ? clamp((start - Math.max(0, (view - size) / 2)) / distance, 0, 1) : 0;
      window.scrollTo(0, state.pinStartY + p * (state.pinEndY - state.pinStartY));
      applyTransforms(p);
    }

    // Whether el is inside its column's box and the window, below the header:
    // fully, or with partly, any of it
    function shown(el, box, partly) {
      var rect = el.getBoundingClientRect();
      var top = Math.max(box.top, state.headerH);
      var bottom = Math.min(box.bottom, window.innerHeight);
      var left = Math.max(box.left, 0);
      var right = Math.min(box.right, window.innerWidth);
      if (partly) return rect.bottom > top && rect.top < bottom && rect.right > left && rect.left < right;
      return rect.top >= top - 1 && rect.bottom <= bottom + 1 && rect.left >= left - 1 && rect.right <= right + 1;
    }

    // Chrome and Firefox focus a link on mousedown, and phones focus inputs
    // on tap. That focus lands where the visitor pointed, so it must not
    // scroll the page: the click would land on whatever moved under it.
    function onPointerDown() {
      pointerAt = Date.now();
    }

    // Keyboard focus: the browser scrolls the clipped column itself to show
    // the focused element. Undo that and, if the element still isn't showing,
    // scroll the page to the pin progress that shows it. Runs a frame later,
    // after the browser's own scroll.
    function onFocusIn(event) {
      if (!pinnedRegistered) return;
      var target = event.target;
      var column = target.closest('[data-eg-story], [data-eg-products]');
      if (!column) return;
      var keyboard = Date.now() - pointerAt > 1000 && focusVisible(target);
      cancelAnimationFrame(focusFrame);
      focusFrame = requestAnimationFrame(function () {
        if (!pinnedRegistered || document.activeElement !== target) return;
        resetScroll();
        // The browser may have scrolled the page too: judge from its new position
        onPinnedTick();
        if (!keyboard) return;
        var box = column.getBoundingClientRect();
        var item = target;
        var whole = target.closest('.editorial-grid__story-block, .editorial-grid__card');
        // The keyboard focus ring goes round the whole card, so show all of it
        // (or the whole story block) when it fits
        if (whole) {
          var rect = whole.getBoundingClientRect();
          if (rect.height <= box.height && rect.width <= box.width) item = whole;
        }
        if (!shown(item, box)) reveal(item);
      });
    }

    function focusVisible(el) {
      try {
        return el.matches(':focus-visible');
      } catch (e) {
        return true;
      }
    }

    // Theme editor: show the selected story block or card. The editor selects
    // the block again after every settings re-render (load is true); if any
    // of it is still showing, leave the page where the merchant scrolled it.
    function select(blockId, load) {
      clearTimeout(selectTimer);
      selectTimer = setTimeout(function () {
        var block = sectionEl.querySelector('[data-block-id="' + blockId + '"]');
        // Product blocks are display: contents, so use their first card
        var item = block && block.classList.contains('editorial-grid__block-products')
          ? block.querySelector('.editorial-grid__card')
          : block;
        if (!item) return;
        if (!pinnedRegistered) {
          var rect = item.getBoundingClientRect();
          if (load && rect.bottom > 0 && rect.top < window.innerHeight) return;
          return item.scrollIntoView({ block: 'center', inline: 'nearest' });
        }
        resetScroll();
        measurePinned();
        onPinnedTick();
        var box = item.closest('[data-eg-story], [data-eg-products]').getBoundingClientRect();
        if (shown(item, box, load)) return;
        reveal(item);
      }, 250);
    }

    function cleanup() {
      clearTimeout(resizeTimer);
      clearTimeout(selectTimer);
      cancelAnimationFrame(focusFrame);
      sectionEl.removeEventListener('focusin', onFocusIn);
      sectionEl.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('load', remeasure);
      mobileQuery.removeEventListener('change', activate);
      if (observer) observer.disconnect();
      unregisterPinned();
      detachTouch();
      storyTrack.style.transform = '';
      productsTrack.style.transform = '';
      sectionEl._eg = null;
    }

    window.addEventListener('resize', onResize, { passive: true });
    // Remeasure after window 'load' so lazy-loaded card images and font
    // swaps don't leave story / product distances stale.
    window.addEventListener('load', remeasure);
    mobileQuery.addEventListener('change', activate);
    sectionEl.addEventListener('focusin', onFocusIn);
    sectionEl.addEventListener('pointerdown', onPointerDown, { passive: true });
    // Late images, fonts and app blocks change the tracks; content above the
    // section can change height after load; a new --header-height resizes
    // the columns. None of these fire a window resize.
    if (window.ResizeObserver) {
      observer = new ResizeObserver(function () { remeasure(); });
      observer.observe(document.body);
      [storyTrack, productsTrack, storyContainer, productsContainer].forEach(function (el) {
        observer.observe(el);
      });
    }

    sectionEl._eg = { cleanup: cleanup, measure: remeasure, select: select };
    activate();
  }

  function init(root) {
    if (!root || !root.classList || !root.classList.contains('editorial-grid')) return;
    if (root._eg || root._egWaiting) return;
    root.classList.add('editorial-grid--js');
    root._egWaiting = true;
    withParallax(root.getAttribute('data-parallax-src'), function (ok) {
      root._egWaiting = false;
      if (!root.isConnected || root._eg) return;
      // Nothing moves the tracks without parallax.js: show the unpinned layout
      if (!ok) return root.classList.remove('editorial-grid--js');
      try {
        setupSection(root);
      } catch (error) {
        // A browser missing something setup needs: unpinned beats a frozen pin
        root.classList.remove('editorial-grid--js');
        throw error;
      }
    });
  }

  function rootsIn(el) {
    return el && el.querySelectorAll ? el.querySelectorAll('.editorial-grid') : [];
  }

  // Theme editor listeners, registered once for every instance on the page
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      Array.prototype.forEach.call(rootsIn(event.target), init);
    });
    document.addEventListener('shopify:section:unload', function (event) {
      Array.prototype.forEach.call(rootsIn(event.target), function (el) {
        if (el._eg) el._eg.cleanup();
      });
    });
    // Moving a section changes the positions without resizing anything.
    // Measure a frame later: a moved section's stylesheet can take that long
    // to apply again, and the ResizeObserver catches it if it takes longer.
    document.addEventListener('shopify:section:reorder', function () {
      requestAnimationFrame(function () {
        Array.prototype.forEach.call(document.querySelectorAll('.editorial-grid'), function (el) {
          if (el._eg) el._eg.measure();
        });
      });
    });
    document.addEventListener('shopify:block:select', function (event) {
      var el = event.target && event.target.closest ? event.target.closest('.editorial-grid') : null;
      if (el && el._eg) el._eg.select(event.detail.blockId, event.detail.load);
    });
  }

  window.EditorialGrid = { init: init };
})();
